// Static metadata for the Connections surfaces (top-bar pill, the
// /settings/integrations page): what each integration IS - label, group, scope
// disclosure. Connection STATE is never declared here: rows the server
// can vouch for start as "unknown" and are resolved by
// resolveIntegrations() from real probe answers. Raw scope lists arrive
// on the wire and beat the copies below; the copies remain only as the
// offline fallback.

import type { Integration, IntegrationGroup, IntegrationStatus } from "./types";
import type { ProviderState, ProviderStatus } from "../api/integrations";

export const INTEGRATIONS: Integration[] = [
  // Google, service-account family: no client-reachable probe exists yet
  // (the connections service arrives with the DB-config modules), so the
  // honest render is "Not verified", not "Live".
  {
    id: "gws",
    group: "google-sa",
    label: "Google Workspace",
    iconKind: "gws",
    status: "unknown",
    auth: "service-account",
    scope: "Firm Workspace domain",
    accessSummary: "Firm domain link - provides shared-drive + directory access",
    accessGranted: ["Shared drive enumeration", "Directory user list"],
    accessNeverGranted: [
      "Personal Gmail",
      "Personal Drive",
      "Drive contents outside Shared drives",
    ],
  },
  {
    id: "gdrive",
    group: "google-sa",
    label: "Google Drive",
    iconKind: "drive",
    status: "unknown",
    auth: "service-account",
    scope: "Shared Drives the service account is invited to",
    accessSummary:
      "Reads case files in Shared drives; auto-classifies anything dropped to DuraLex Inbox.",
    accessGranted: [
      "Full Drive scope - reaches only Shared drives the service account is invited to",
      "Read files in /DuraLex Inbox/",
    ],
    accessNeverGranted: ["Personal Drive", "Shared drives the account was never invited to"],
    rawScopes: ["https://www.googleapis.com/auth/drive"],
  },
  {
    id: "gsheets",
    group: "google-sa",
    label: "Master workbook",
    iconKind: "sheets",
    status: "unknown",
    auth: "service-account",
    scope: "The one workbook whose id the firm sets in Settings",
    accessSummary:
      "Reads the master case list by its id - a Drive file, whether it is an uploaded workbook or a Sheet.",
    accessGranted: ["Read the workbook set in Settings"],
    accessNeverGranted: [
      "Any other spreadsheet",
      "Writing to the workbook - the import only reads",
    ],
    rawScopes: ["https://www.googleapis.com/auth/drive"],
  },

  // Google, OAuth family: one consent grant covers all three, so the
  // /api/integrations/status aggregate resolves them together.
  {
    id: "gcal",
    group: "google-oauth",
    label: "Google Calendar",
    iconKind: "calendar",
    status: "unknown",
    auth: "oauth",
    scope: "Firm calendars the grant can read",
    accessSummary: "Parses hearing notices into structured deadlines. Read-only.",
    accessGranted: ["Read events - via the staff grant or the firm service account"],
    accessNeverGranted: [
      "Creating or modifying events - read-only scope",
      "External attendee personal calendars",
    ],
    rawScopes: ["https://www.googleapis.com/auth/calendar.readonly"],
  },
  {
    id: "gcontacts",
    group: "google-oauth",
    label: "Google Contacts",
    iconKind: "contacts",
    status: "unknown",
    auth: "oauth",
    scope: "Firm directory + the connected account's contacts",
    accessSummary: "Surfaces opposing counsel + experts for matter assignment.",
    accessGranted: [
      "Read contacts (directory + personal) - staff grant or firm service account",
      "Suggest matches",
    ],
    accessNeverGranted: ["Modify contacts"],
    rawScopes: ["https://www.googleapis.com/auth/contacts.readonly"],
  },
  {
    id: "gmail",
    group: "google-oauth",
    label: "Gmail",
    iconKind: "gmail",
    status: "unknown",
    auth: "oauth",
    // The mailbox identity is server truth ("Connected as ..."), not a
    // fixture string - describe the surface instead.
    scope: "Firm mailbox threads + attachments",
    accessSummary:
      "Read recent threads from clients + agencies (USCIS, EOIR). Send drafts on request only.",
    accessGranted: ["gmail.readonly", "gmail.send (one-tap confirmation)"],
    accessNeverGranted: [
      "gmail.modify outside DuraLex-tagged threads",
      "Personal Gmail outside firm domain",
    ],
    rawScopes: [
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.send",
    ],
  },

  // Roadmap - no probe exists, so no status string beyond that is
  // honest, and no access disclosure either: nothing is accessed yet.
  // Each plan carries the block it would join if built, so a row that
  // ever leaves the roadmap has a visible home on day one.
  {
    id: "gtranslate",
    group: "google-sa",
    label: "Google Translate",
    iconKind: "translate",
    status: "roadmap",
    auth: "api-key",
    scope: "Multilingual client comms (Russian, Ukrainian, Spanish)",
  },

  // Roadmap
  {
    id: "gdocs",
    group: "google-oauth",
    label: "Google Docs",
    iconKind: "docs",
    status: "roadmap",
    scope: "Live brief drafting w/ presence",
  },
  {
    id: "gtasks",
    group: "google-oauth",
    label: "Google Tasks",
    iconKind: "tasks",
    status: "roadmap",
    scope: "Deadline sync to staff task lists",
  },
  {
    id: "gmeet",
    group: "google-oauth",
    label: "Google Meet",
    iconKind: "meet",
    status: "roadmap",
    scope: "Auto-attached to events; transcripts",
  },
  {
    id: "gvoice",
    group: "google-oauth",
    label: "Google Voice",
    iconKind: "voice",
    status: "roadmap",
    scope: "Call/SMS via Gmail-forward parsing",
  },

  // Microsoft 365 - the first non-Google storage connector on the
  // roadmap (docs/CONNECTOR_ROADMAP.md). Same row shape as the Google
  // modules from day one, so shipping it is a status change, not a UI.
  {
    id: "m365",
    group: "m365",
    label: "Microsoft 365",
    iconKind: "m365",
    status: "roadmap",
    scope: "Outlook mail, OneDrive files, calendars - the Google-parallel connector",
  },

  // The server's "uscis" row describes the federal form catalogue (the
  // FormTemplate table + uscis.gov version checks) - the row says that,
  // not the un-built receipt-notice puller.
  {
    id: "uscis",
    group: "gov",
    label: "USCIS forms",
    iconKind: "uscis",
    status: "unknown",
    scope: "Official form PDFs + version tracking from uscis.gov",
    accessSummary:
      "Downloads official form templates from uscis.gov and tracks version changes. No case data leaves DuraLex.",
  },
  {
    id: "eoir",
    group: "gov",
    label: "EOIR ACIS",
    iconKind: "eoir",
    status: "roadmap",
    scope: "Hearing schedule, IJ assignments",
  },
  {
    id: "pacer",
    group: "gov",
    label: "PACER / CM-ECF",
    iconKind: "pacer",
    status: "roadmap",
    scope: "Federal docket pulls",
  },
  {
    id: "midpage",
    group: "research",
    label: "Midpage citations",
    iconKind: "midpage",
    status: "roadmap",
    scope: "Case-law verification (Excel report)",
  },
  {
    id: "stripe",
    group: "billing",
    label: "Stripe Checkout",
    iconKind: "stripe",
    status: "unknown",
    scope: "Client invoice payments (hosted Checkout)",
    accessSummary:
      "Creates hosted Checkout sessions for open invoices; card details never touch DuraLex.",
  },

  {
    id: "anthropic",
    group: "ai",
    label: "Anthropic Claude",
    iconKind: "anthropic",
    status: "unknown",
    auth: "api-key",
    scope: "Document extraction + drafting (three model tiers)",
    accessSummary:
      "Sends document text and case context to the Claude API for extraction and drafting.",
  },
  {
    id: "portal",
    group: "portal",
    label: "Client sign-in policy",
    iconKind: "portal",
    status: "unknown",
    scope: "Two-step, emailed code and case ID sign-in at the client portal door",
    accessSummary:
      "Policy only - which credentials the portal accepts. Nothing external is accessed.",
  },
  {
    id: "mail",
    group: "mail",
    label: "Email sending",
    // Not the Gmail mark: the same row serves a firm sending over its own
    // SMTP host, and a Google glyph there would name the wrong provider.
    // For the same reason it declares no auth kind - the firm picks it.
    iconKind: "email",
    status: "unknown",
    scope: "Whatever the firm chose - its Google mailbox or its own mail server",
    accessSummary:
      "Everything DuraLex sends leaves through one sender the firm picks. Incoming mail is a separate connection.",
  },
];

export const INTEGRATION_GROUPS: IntegrationGroup[] = [
  {
    id: "google-sa",
    label: "Google - firm service account",
    subtitle: "Workspace domain, Drive, Sheets - one key the firm invites into its Shared Drives",
  },
  {
    id: "google-oauth",
    label: "Google - staff grant",
    subtitle: "Gmail, Calendar, Contacts - one consent covers all three",
  },
  { id: "m365", label: "Microsoft 365", subtitle: "Outlook, OneDrive, SharePoint" },
  { id: "gov", label: "Government systems", subtitle: "USCIS, EOIR, PACER" },
  { id: "research", label: "Research", subtitle: "Case law verification" },
  { id: "billing", label: "Billing", subtitle: "Payments + invoicing" },
  { id: "ai", label: "AI", subtitle: "Claude document intelligence" },
  {
    id: "mail",
    label: "Outgoing email",
    subtitle: "One sender for invitations, notices and password resets",
  },
  {
    id: "portal",
    label: "Client portal",
    subtitle: "How clients sign in - the firm's policy, not a connection",
  },
];

// The server's four-value state maps onto the render vocabulary; rows the
// aggregate does not mention keep their static status.
const SERVER_STATE_TO_STATUS: Record<ProviderState, IntegrationStatus> = {
  connected: "live",
  configured: "configured",
  action_required: "action_required",
  not_configured: "not_configured",
};

/** Overlay the aggregate's answers on the static rows. Only a server
 *  verdict may claim "live"; no answer leaves a row "unknown". */
export function resolveIntegrations(providers: ProviderStatus[] | undefined): Integration[] {
  if (!providers || providers.length === 0) return INTEGRATIONS;
  const byId = new Map(providers.map((p) => [p.id, p]));
  return INTEGRATIONS.map((raw) => {
    const p = byId.get(raw.id);
    if (!p) return raw;
    return {
      ...raw,
      // A state this bundle predates degrades to "unknown", never to a
      // label the server did not say.
      status: (SERVER_STATE_TO_STATUS[p.state] as IntegrationStatus | undefined) ?? "unknown",
      detail: p.detail,
      lastSyncAt: p.lastSyncAt,
      account: p.account,
      // The server's scope constants beat the bundle's copy - the wire
      // cannot go stale, the fixture can.
      rawScopes: p.scopes ?? raw.rawScopes,
    };
  });
}

export interface IntegrationCounts {
  pillText: string;
  pillTone: "amber" | "green" | "neutral";
}

export const integrationCounts = (list: Integration[] = INTEGRATIONS): IntegrationCounts => {
  const by = (s: string) => list.filter((i) => i.status === s).length;
  const live = by("live");
  const attn = by("action_required") + by("error");
  // No "N of M": the denominator would count roadmap dreams as if they
  // were connectable. Claim only what a probe has verified.
  const pillText =
    attn > 0
      ? `${live} connected - ${attn} need${attn === 1 ? "s" : ""} attention`
      : live > 0
        ? `${live} connected`
        : "Connections";
  const pillTone: "amber" | "green" | "neutral" =
    attn > 0 ? "amber" : live > 0 ? "green" : "neutral";
  return { pillText, pillTone };
};
